import React from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const DeleteConfirmModal = ({ isOpen, user, onClose, onConfirm }) => {
  if (!isOpen || !user) return null;

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'admin': 'Master Admin',
      'clab': 'CLAB User',
      'recruiter': 'Recruiter',
      'employer': 'Employer'
    };
    return roleMap[role] || role;
  };

  return (
    <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-1001 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-error-100 rounded-full flex items-center justify-center mr-3">
              <Icon name="AlertTriangle" size={20} className="text-error" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Delete User</h2>
              <p className="text-sm text-text-secondary">This action cannot be undone</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-md transition-colors duration-150"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <p className="text-text-primary mb-4">
              Are you sure you want to delete this user? This will permanently remove their account and all associated data.
            </p>
            
            {/* User Info Card */}
            <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-secondary-100 flex-shrink-0">
                  <Image
                    src={user.avatar}
                    alt={user.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-text-primary">{user.name}</h3>
                  <p className="text-xs text-text-secondary">{user.email}</p>
                  <div className="flex items-center mt-1">
                    <span className="text-xs text-text-secondary">
                      {getRoleDisplayName(user.role)}
                    </span>
                    {user.assignedRecruiter && (
                      <>
                        <span className="text-xs text-text-secondary mx-1">•</span>
                        <span className="text-xs text-text-secondary">
                          Assigned to {user.assignedRecruiter}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Warning Messages */}
          <div className="space-y-3 mb-6">
            <div className="flex items-start p-3 bg-warning-50 border border-warning-200 rounded-lg">
              <Icon name="AlertCircle" size={16} className="text-warning-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-warning-800">
                <p className="font-medium">Data Loss Warning</p>
                <p>All user data, including login history and associated records, will be permanently deleted.</p>
              </div>
            </div>

            {user.role === 'recruiter' && (
              <div className="flex items-start p-3 bg-error-50 border border-error-200 rounded-lg">
                <Icon name="Users" size={16} className="text-error-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-error-800">
                  <p className="font-medium">Recruiter Dependencies</p>
                  <p>This recruiter may have assigned employers and biodata records. Consider reassigning them first.</p>
                </div>
              </div>
            )}

            {user.role === 'employer' && user.assignedRecruiter && (
              <div className="flex items-start p-3 bg-accent-50 border border-accent-200 rounded-lg">
                <Icon name="FileText" size={16} className="text-accent-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-accent-800">
                  <p className="font-medium">Biodata Records</p>
                  <p>Any biodata selections or shortlists associated with this employer will be affected.</p>
                </div>
              </div>
            )}
          </div>

          {/* Confirmation Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Type <span className="font-mono bg-secondary-100 px-1 rounded">DELETE</span> to confirm:
            </label>
            <input
              type="text"
              placeholder="Type DELETE to confirm"
              className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-error-500 focus:border-error-500 transition-all duration-150"
              onChange={(e) => {
                const deleteButton = document.getElementById('confirm-delete-btn');
                if (deleteButton) {
                  deleteButton.disabled = e.target.value !== 'DELETE';
                }
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-secondary-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-secondary-700 bg-secondary-100 rounded-lg hover:bg-secondary-200 transition-colors duration-150"
          >
            Cancel
          </button>
          <button
            id="confirm-delete-btn"
            onClick={onConfirm}
            disabled
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-error rounded-lg hover:bg-error-700 disabled:bg-secondary-300 disabled:cursor-not-allowed transition-colors duration-150"
          >
            <Icon name="Trash2" size={16} className="mr-2" />
            Delete User
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmModal;